import React, { useCallback, useEffect, useMemo, useState } from "react";
import SavedLocationCombobox from "./SavedLocationCombobox";
import { serverInfo } from "../shared/config/serverConfig";


import { apiFetch } from "../shared/api/apiFetch";

/**
 * Reusable “recent values” combobox backed by server RecentEntityUsage.
 * - Lets user type any value or pick from list
 * - On blur (or picking an option), records value to server
 * - Server prunes to max 5 per (account_id, entity_type)
 */
export default function RecentEntityCombobox({
  accountId,
  entityType,
  value,
  onChange,
  disabled = false,
  placeholder,
  style,
  id,
  title,
}) {
  const [suggestions, setSuggestions] = useState([]);

  const serverUrl = serverInfo.serverUrl;

  const load = useCallback(() => {
    if (!accountId || !entityType) {
      setSuggestions([]);
      return;
    }
    apiFetch(`${serverUrl}/recentEntityUsage/list`, {
      method: "POST",
      body: JSON.stringify({ account_id: accountId, entity_type: entityType, limit: 5 }),
    })
      .then((r) => (r.ok ? r.json() : r.text().then((t) => Promise.reject(new Error(t || r.status)))))
      .then((data) => setSuggestions(Array.isArray(data.values) ? data.values : []))
      .catch(() => setSuggestions([]));
  }, [accountId, entityType, serverUrl]);

  useEffect(() => {
    load();
  }, [load]);

  const record = useCallback(
    (raw) => {
      const v = (raw != null ? String(raw) : "").trim();
      if (!v || !accountId || !entityType) return Promise.resolve();
      return apiFetch(`${serverUrl}/recentEntityUsage/record`, {
        method: "POST",
        body: JSON.stringify({ account_id: accountId, entity_type: entityType, entity_value: v }),
      })
        .then((r) => (r.ok ? r.json() : r.text().then((t) => Promise.reject(new Error(t || r.status)))))
        .then(() => load())
        .catch(() => undefined);
    },
    [accountId, entityType, serverUrl, load]
  );

  const mergedPlaceholder = useMemo(() => placeholder || "Type or pick…", [placeholder]);

  return (
    <SavedLocationCombobox
      id={id}
      value={value}
      onChange={(v) => {
        if (onChange) onChange(v);
      }}
      onPick={(picked) => record(picked)}
      onBlurPersist={(v) => record(v)}
      suggestions={suggestions}
      disabled={disabled}
      placeholder={mergedPlaceholder}
      style={style}
      title={title}
    />
  );
}

